import React from 'react';
import {
  Table, TableHead, TableRow, TableCell,
  TableBody, Box
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Typography from '@mui/material/Typography';
import { formatDate } from '../utils/timeUtils.js';


const getCaretIndexWithin = (containerEl) => {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) {
    console.log('[getCaretIndexWithin] no selection or rangeCount=0');
    return null;
  }
  const range = sel.getRangeAt(0);
  const containsStart = containerEl.contains(range.startContainer);
  const containsEnd = containerEl.contains(range.endContainer);
  console.log('[getCaretIndexWithin] sel:', {
    type: sel.type,
    anchorOffset: sel.anchorOffset,
    focusOffset: sel.focusOffset,
    rangeText: range.toString(),
    containsStart,
    containsEnd
  });
  if (!containsStart && !containsEnd) {
    console.log('[getCaretIndexWithin] selection is outside this cell');
    return null;
  }

  // selection start index
  const preStart = range.cloneRange();
  preStart.selectNodeContents(containerEl);
  preStart.setEnd(range.startContainer, range.startOffset);
  const startIndex = preStart.toString().length;

  // selection end index
  const preEnd = range.cloneRange();
  preEnd.selectNodeContents(containerEl);
  preEnd.setEnd(range.endContainer, range.endOffset);
  const endIndex = preEnd.toString().length;

  const mid = Math.floor((startIndex + endIndex) / 2);
  console.log('[getCaretIndexWithin] startIndex:', startIndex, 'endIndex:', endIndex, 'mid:', mid);
  return mid;
};

const expandByDelimiters = (text, caret) => {
  if (!text || caret == null) {
    console.log('[expandByDelimiters] empty text or caret null');
    return '';
  }
  // begin of the row (-1)
  const leftIdxsRaw = [
    text.lastIndexOf(',', caret - 1),
    text.lastIndexOf(':', caret - 1),
    text.lastIndexOf(';', caret - 1),
    text.lastIndexOf('\n', caret - 1),
    text.lastIndexOf('\r', caret - 1),
    text.lastIndexOf('(', caret - 1),
    -1
  ];

  const dotSpaceLeft = text.lastIndexOf('. ', caret - 1);
  if (dotSpaceLeft !== -1) leftIdxsRaw.push(dotSpaceLeft + 1);

  const left = Math.max(...leftIdxsRaw);

  const rightQ = text.indexOf('?', caret);
  if (rightQ !== -1) {
    let slice = text.slice(left + 1, rightQ + 1);
    slice = slice.trim();
    if (slice.startsWith('(')) slice = slice.slice(1).trimStart();
    if (slice.endsWith(')')) slice = slice.slice(0, -1).trimEnd();
    slice = slice.replace(/^[,;:]\s*/, '').replace(/\s*[,;:]$/, '');
    console.log('[expandByDelimiters]', { caret, left, rightQ_inclusive: rightQ + 1, slice });
    return slice;
  }

  const rightIdxsRaw = [
    text.indexOf(',', caret),
    text.indexOf(':', caret),
    text.indexOf(';', caret),
    text.indexOf('\n', caret),
    text.indexOf('\r', caret),
    text.indexOf(')', caret),
  ].filter(i => i !== -1);

  const dotSpaceRight = text.indexOf('. ', caret);
  if (dotSpaceRight !== -1) rightIdxsRaw.push(dotSpaceRight);

  const right = rightIdxsRaw.length ? Math.min(...rightIdxsRaw) : text.length;

  let slice = text.slice(left + 1, right);

  slice = slice.trim();
  if (slice.startsWith('(')) slice = slice.slice(1).trimStart();
  if (slice.endsWith(')')) slice = slice.slice(0, -1).trimEnd();
  slice = slice.replace(/^[,;:]\s*/, '').replace(/\s*[,;:]$/, '');

  console.log('[expandByDelimiters]', { caret, left, right, slice });
  return slice;
};

const getTooltipBackgroundColor = (tooltipText) => {
  switch (tooltipText) {
    case 'Analysis DONE':
      return '#b6ebd2';
    case 'Partially analysed':
      return '#ffd9a6';
    case 'NOT analysed':
    default:
      return '#f0f0f0';
  }
};

const PatientTableView = ({ data, auswertungen, onRowClick, getRowColor, isStreaming, isTitleChoice, isTreatmentStepsChoice, onSelectText, selectedText, hasBestPractice }) => {
  const [tooltipText, setTooltipText] = React.useState('');
  const [tooltipPos, setTooltipPos] = React.useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = React.useState(false);
  const tooltipRaf = React.useRef(null);
  const pendingMousePos = React.useRef({ x: 0, y: 0 });
  const tooltipDelayTimer = React.useRef(null);

  const isInBestPracticeCell = (e) => {
    return Boolean(e?.target?.closest?.('[data-bp-cell="1"]'));
  };

  return (
    <>
      {!isStreaming && auswertungen.length === 0 && !isTitleChoice && !isTreatmentStepsChoice && (
        <Box sx={{
          color: 'black',
          backgroundColor: '#fff8c4',
          padding: 2,
          marginBottom: 2,
          borderRadius: 2,
          border: '1px solid #f0c000',
          fontWeight: 'bold'
        }}>
          ⚠️ No doctors analysis "doks_auswertungen" found!
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ verticalAlign: 'top' }}>Status</TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>Patienten</TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>Datum</TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>Titel</TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>Best practice</TableCell>
            <TableCell sx={{ verticalAlign: 'top' }}>Info</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(data) && data.map((row, idx) => {
            const idDbg = row?.id ?? row?.llm_analysis_result_id;
            let hasDbg = null;
            try {
              hasDbg = typeof hasBestPractice === 'function' ? hasBestPractice(row) : 'NO_FN';
            } catch (e) {
              hasDbg = 'ERR';
            }
            if (idx < 5) {
              console.log('[BP DBG row]', { idx, idDbg, hasDbg, bp_in_row: row?.best_practice_template });
            }
            const color = getRowColor(row);

            let tooltipTextValue = '';
            if (color === '#e6f4ea') {
              tooltipTextValue = 'Analysis DONE';
            } else if (color === '#fff4e5') {
              tooltipTextValue = 'Partially analysed';
            } else {
              tooltipTextValue = 'NOT analysed';
            }

            return (
              <TableRow
                key={idx}
                hover
                onClick={(e) => onRowClick(e, row)}
                onMouseMove={(e) => {
                  if (isInBestPracticeCell(e)) {
                    if (showTooltip) setShowTooltip(false);
                    setTooltipText('');
                    if (tooltipDelayTimer.current) {
                      clearTimeout(tooltipDelayTimer.current);
                      tooltipDelayTimer.current = null;
                    }
                    return;
                  }
                  pendingMousePos.current = { x: e.clientX + 12, y: e.clientY + 12 };
                  if (!showTooltip && !tooltipDelayTimer.current) {
                    tooltipDelayTimer.current = setTimeout(() => {
                      setShowTooltip(true);
                      setTooltipText(tooltipTextValue);
                      tooltipDelayTimer.current = null;
                    }, 200);
                  }
                  if (!tooltipRaf.current) {
                    tooltipRaf.current = requestAnimationFrame(() => {
                      setTooltipPos(pendingMousePos.current);
                      tooltipRaf.current = null;
                    });
                  }
                }}
                onMouseLeave={() => {
                  setShowTooltip(false);
                  setTooltipText('');
                  if (tooltipDelayTimer.current) {
                    clearTimeout(tooltipDelayTimer.current);
                    tooltipDelayTimer.current = null;
                  }
                  if (tooltipRaf.current) {
                    cancelAnimationFrame(tooltipRaf.current);
                    tooltipRaf.current = null;
                  }
                }}
                sx={{
                  backgroundColor: color,
                  cursor: 'pointer',
                  '&:hover td': {
                    backgroundColor:
                      color === '#e6f4ea'
                        ? '#b6ebd2'
                        : color === '#fff4e5'
                          ? '#ffd9a6'
                          : undefined
                  }
                }}
              >
                <TableCell align="center">
                  {color === '#e6f4ea' && (
                    <CheckCircleIcon sx={{ fontSize: 32, color: 'green' }} />
                  )}
                  {color === '#fff4e5' && (
                    <CheckCircleIcon sx={{ fontSize: 32, color: 'orange' }} />
                  )}
                </TableCell>

                <TableCell>{row.patnr_id}</TableCell>
                <TableCell>{formatDate(row.time)}</TableCell>
                <TableCell sx={{ maxWidth: 380, whiteSpace: 'normal' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {row.extracted_title?.trim() || '…'}
                  </Typography>
                </TableCell>
                <TableCell align="center" data-bp-cell="1">
                  {typeof hasBestPractice === 'function' ? (
                    hasBestPractice(row) ? (
                      <Tooltip title="Best practice template exists">
                        <span
                          style={{
                            fontSize: 24,
                            lineHeight: 1,
                            display: 'inline-block',
                            transform: 'translateY(1px)',
                            userSelect: 'none'
                          }}
                          aria-label="Best practice"
                        >
                          📄
                        </span>
                      </Tooltip>
                    ) : (
                      <span
                        style={{
                          fontSize: 16,
                          lineHeight: 1,
                          display: 'inline-block',
                          transform: 'translateY(1px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                        aria-label="No best practice"
                      >
                        ➖
                      </span>
                    )
                  ) : (
                    <span
                      style={{
                        fontSize: 16,
                        lineHeight: 1,
                        display: 'inline-block',
                        transform: 'translateY(1px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                      aria-label="No best practice"
                    >
                      ➖
                    </span>
                  )}
                </TableCell>
                <TableCell
                  onDoubleClick={(e) => {
                    const infoText = String(row.info || '');
                    e.preventDefault();
                    e.stopPropagation(); // against onMouseClick
                    console.log('[onMouseUp] infoText.len=', infoText.length, 'text=', JSON.stringify(infoText));
                  
                    const caret = getCaretIndexWithin(e.currentTarget);
                    console.log('[onMouseUp] caret from selection:', caret);
                  
                    // fallback
                    if (caret == null) {
                      const sel = window.getSelection?.();
                      try {
                        const rangeFromPoint =
                          document.caretRangeFromPoint?.(e.clientX, e.clientY) ||
                          document.caretPositionFromPoint?.(e.clientX, e.clientY);
                        if (rangeFromPoint) {
                          const pre = document.createRange();
                          pre.selectNodeContents(e.currentTarget);
                          // API caretPositionFromPoint returns {offsetNode, offset}
                          if ('offsetNode' in rangeFromPoint) {
                            pre.setEnd(rangeFromPoint.offsetNode, rangeFromPoint.offset);
                          } else {
                            pre.setEnd(rangeFromPoint.endContainer, rangeFromPoint.endOffset);
                          }
                          const caret2 = pre.toString().length;
                          console.log('[onMouseUp] caret from point:', caret2);
                          const phrase2 = expandByDelimiters(infoText, caret2);
                          console.log('[onMouseUp] phrase2:', JSON.stringify(phrase2));
                          if (phrase2) onSelectText?.(phrase2);
                          return;
                        }
                      } catch (err) {
                        console.log('[onMouseUp] point fallback error:', err);
                      }
                      console.log('[onMouseUp] caret still null, abort');
                      return;
                    }
                  
                    const phrase = expandByDelimiters(infoText, caret);
                    console.log('[onMouseUp] final phrase:', JSON.stringify(phrase));
                    if (phrase) onSelectText?.(phrase);
                  }}
                >
                  {row.info}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {showTooltip && tooltipText && (
        <Box
          sx={{
            position: 'fixed',
            top: tooltipPos.y,
            left: tooltipPos.x,
            backgroundColor: getTooltipBackgroundColor(tooltipText),
            border: '1px solid #999',
            padding: '8px 12px',
            fontSize: '1.2rem',
            color: 'black',
            borderRadius: 2,
            maxWidth: 400,
            whiteSpace: 'pre-wrap',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            zIndex: 9999,
            pointerEvents: 'none',
            opacity: 1,
            transition: 'top 0.05s linear, left 0.05s linear, opacity 0.2s ease-in'
          }}
        >
          {tooltipText}
        </Box>
      )}
    </>
  );
};

export default PatientTableView;